// اجزای مشترک صفحات تنظیمات PLT (AP-PLT-02): پیام فقط‌خواندنی، کلیدهای Query و داده مرجع GEO_DIVISIONS.
import Alert from '@mui/material/Alert';
import { unwrap, type ReferenceItem } from '@phoenix/api-client';
import { useTenantSession, type WriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * گروه رجیستری تنظیمات نمایش PLT (PlatformSettingsProvider: plt.display_currency_unit و plt.default_timezone). کلید تنظیم
 * پیشوند plt دارد ولی گروه «platform.localization» است؛ GET ?group=plt خالی برمی‌گرداند (C-AGENT-206).
 */
export const PLT_DISPLAY_SETTINGS_GROUP = 'platform.localization';

export const pltKeys = {
  profile: (tenantId: string | undefined) => ['platform', 'tenant', 'profile', tenantId] as const,
  branches: (tenantId: string | undefined) => ['platform', 'branches', tenantId] as const,
  geo: ['platform', 'reference-data', 'GEO_DIVISIONS'] as const,
  settings: (tenantId: string | undefined) =>
    ['configuration', 'settings', 'group', PLT_DISPLAY_SETTINGS_GROUP, tenantId] as const,
  // AP-PLT-05.
  branding: (tenantId: string | undefined) => ['platform', 'branding', tenantId] as const,
  holidays: (tenantId: string | undefined) => ['platform', 'calendar', 'holidays', tenantId] as const,
  publicProfile: (tenantId: string | undefined) => ['platform', 'public-profile', tenantId] as const,
  smsCredit: (tenantId: string | undefined) => ['platform', 'sms-credit', tenantId] as const,
  usage: (tenantId: string | undefined) => ['platform', 'usage', tenantId] as const,
  supportAccess: (tenantId: string | undefined) => ['platform', 'support-access', tenantId] as const,
  checklist: (tenantId: string | undefined) => ['platform', 'onboarding', 'checklist', tenantId] as const,
  goLive: (tenantId: string | undefined) => ['platform', 'onboarding', 'go-live', tenantId] as const,
  announcements: (tenantId: string | undefined) => ['platform', 'announcements', 'active', tenantId] as const,
  providers: (tenantId: string | undefined) => ['identity', 'providers', 'active', tenantId] as const,
  // AP-PLT-08: وضعیت لایسنس On-prem (نوار هشدار و SCR-STAFF-910).
  license: (tenantId: string | undefined) => ['platform', 'license', tenantId] as const,
};

/** int64/int32 سرور ممکن است رشته باشد (OpenAPI)؛ مقادیر شمارشی کوچک‌اند و به number تبدیل می‌شوند. */
export function toCount(value: number | string | null | undefined): number {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** دلیل غیرفعال بودن نوشتن (بدون Permission یا حالت دسترسی غیر Full). */
export function WriteNotice({ write }: { write: WriteAccess }) {
  const { t } = useTranslation('plt');
  if (write.canWrite) {
    return null;
  }
  return (
    <Alert
      severity="info"
      data-testid="write-disabled-notice"
      data-reason={write.blockedByAccessMode ? 'access_mode' : 'permission'}
    >
      {t(write.blockedByAccessMode ? 'plt:write_notice.access_mode' : 'plt:write_notice.permission')}
    </Alert>
  );
}

export interface GeoDivisions {
  provinces: { key: string; name: string }[];
  citiesOf: (province: string | null | undefined) => { key: string; name: string }[];
  name: (key: string | null | undefined) => string | undefined;
}

function itemName(item: ReferenceItem): string {
  const data = item.data as { name_fa?: unknown; name?: unknown } | null;
  const name = data?.name_fa ?? data?.name;
  return typeof name === 'string' ? name : item.key;
}

/** استان و شهر از داده مرجع GEO_DIVISIONS (نسخه مؤثر امروز؛ فقط خواندن — BR-PLT-101). */
export function useGeoDivisions(): { geo: GeoDivisions; isPending: boolean; error: unknown } {
  const { tenantApi } = useTenantSession();
  const query = useQuery({
    queryKey: pltKeys.geo,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/platform/reference-data/{dataset}', {
          params: { path: { dataset: 'GEO_DIVISIONS' }, query: { limit: 500 } },
        }),
      ),
    staleTime: 60 * 60_000,
  });
  const geo = useMemo<GeoDivisions>(() => {
    const items = query.data?.items ?? [];
    const typed = (type: string) =>
      items.filter((item) => (item.data as { type?: unknown } | null)?.type === type);
    const provinces = typed('province')
      .map((item) => ({ key: item.key, name: itemName(item) }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fa'));
    const cities = typed('city').map((item) => ({
      key: item.key,
      name: itemName(item),
      province: (item.data as { province?: unknown }).province,
    }));
    const names = new Map(items.map((item) => [item.key, itemName(item)]));
    return {
      provinces,
      citiesOf: (province) =>
        cities.filter((city) => city.province === province).map(({ key, name }) => ({ key, name })),
      name: (key) => (key ? names.get(key) : undefined),
    };
  }, [query.data]);
  return { geo, isPending: query.isPending, error: query.error };
}
